import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Clock, GraduationCap, CheckCircle2 } from "lucide-react";
import { NDLCS_SKILLS_COURSES } from "@/lib/ndlcs-skills-courses";

export const Route = createFileRoute("/education/skills/$slug")({
  loader: ({ params }) => {
    const course = NDLCS_SKILLS_COURSES.find((c) => c.slug === params.slug);
    if (!course) throw notFound();
    return { course };
  },
  component: SkillsCoursePage,
  head: ({ loaderData }) => {
    const c = loaderData?.course;
    const title = c ? `${c.code} — ${c.title} · NDLCS Academy` : "NDLCS Academy course";
    const desc = c?.shortDescription ?? "Free NDLCS Academy skills course.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
});

function SkillsCoursePage() {
  const { course } = Route.useLoaderData();
  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">
        NDLCS Academy · {course.category}
      </p>
      <h1 className="mt-2 font-display text-3xl text-navy">{course.title}</h1>
      <p className="mt-1 text-xs text-muted-foreground">
        {course.code} · {course.level} · ~{course.estimatedHours} hours
      </p>
      <p className="mt-4 text-base text-muted-foreground">{course.shortDescription}</p>

      <section className="mt-6 rounded-lg border border-input bg-card p-5">
        <h2 className="mb-3 font-display text-lg text-navy">What you'll be able to do</h2>
        <ul className="space-y-2 text-sm">
          {course.outcomes.map((o: string) => (
            <li key={o} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <span>{o}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/portal/academy/$slug"
          params={{ slug: course.slug }}
          className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-navy shadow-sm hover:opacity-90"
        >
          <GraduationCap className="h-4 w-4" /> Open in your portal
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-semibold text-navy hover:bg-muted"
        >
          Sign in to start
        </Link>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" /> Self-paced
        </span>
      </div>

      <div className="mt-8 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
        <strong>Disclaimer:</strong> NDLCS Academy courses are not accredited and do
        not carry NQF / SAQA recognition. Completion earns an NDLCS
        professional-development certificate only.
      </div>
    </main>
  );
}
